import React, {useCallback, useEffect, useRef, useState} from 'react'
import Header from '../../components/Header'
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import {Link} from "react-router-dom";
import {useRecoilState, useRecoilValue} from "recoil";
import {adminState, fetchAPIResponseSelector} from "../../store/user.atom";
import {OrderDTO, OrderState} from "../../dtos/order.dto";
import {toast} from "react-toastify";
import {API_URL} from "../../utils/constants";
import {STATUS_META} from "../../utils/orderStatus";

type Props = {}

const Analytics = (props: Props) => {

  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);

  const [startDate, setStartDate] = useState<Date | undefined>(startOfDay);
  const [endDate, setEndDate] = useState<Date | undefined>(endOfDay);

  const [income, setIncome] = useState(0);
  const [ordersDelivered, setOrdersDelivered] = useState(-0);
  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const observer = useRef<IntersectionObserver | null>(null);
  const [admin, setAdmin] = useRecoilState(adminState);
  const fetchAPIResponse = useRecoilValue(fetchAPIResponseSelector);
  const [orders, setOrders] = useState<OrderDTO[]>([]);
  const [orderState, setOrderState] = useState(OrderState.DELIVERED);

  const lastItemRef = useCallback((node: HTMLDivElement | null) => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && page < totalPages) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [page, totalPages]);

  useEffect(() => {
    fetchOrders(true)
  }, [orderState]);

  useEffect(() => {
    fetchOrders(true)
    fetchAnalytics()
  }, [startDate, endDate]);

  useEffect(() => {
    if(page !== 1) {
      fetchOrders(false)
    }
  }, [page]);

  const fetchAnalytics = async () => {
    try {
      let fetchUrl = `/admins/analytics/?from=${startDate}&to=${endDate}`;

      const response = await fetchAPIResponse<{income: number, orders_delivered: number}>(fetchUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
      });


      setIncome(response.data.income);
      setOrdersDelivered(response.data.orders_delivered);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchOrders = async (reset: boolean = false) => {
    try {
      let fetchUrl = `/orders/?size=${size}&from=${startDate}&to=${endDate}`;

      if(orderState !== undefined) {
        fetchUrl += `&state=${orderState}`
      }

      if(reset) {
        fetchUrl += `&page=1`
      } else {
        fetchUrl += `&page=${page}`
      }

      const response = await fetchAPIResponse<{
        pagination_options: any,
        contents: OrderDTO[]
      }>(fetchUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
      });

      setOrders(prevItems => (reset ? response.data.contents : [...prevItems, ...response.data.contents]));
      setTotalPages(response.data.pagination_options.total_pages);

      if(reset) {
        setPage(1)
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchUserReport = async () => {
    await toast.promise(
        fetch(API_URL + "/admins/users/csv", {
          method: "GET",
          headers: {
            "Accept": "text/csv",
            "Authorization": admin!.token
          }
        }).then(response => response.blob()), // Fetch the response as a Blob
        {
          pending: "Fetching user CSVs...",
          error: "Could not fetch user CSVs",
          success: "Successfully fetched user CSVs"
        }
    ).then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'user_report.csv'; // Set the desired file name
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url); // Clean up the URL object
    }).catch(error => {
      console.error("Error fetching user CSVs:", error);
    });
  }

  return (
    <div className='flex flex-col items-center w-full h-full overflow-auto py-10 gap-6'>
      <Header title={'Analytics'} button={false} buttonTitle={''} action={''} />

      {/* Date range + report toolbar */}
      <div className="zbh-card w-11/12 p-5">
        <div className="flex flex-wrap items-end gap-5">
          <div className="flex flex-col">
            <label htmlFor="startDate" className="zbh-eyebrow mb-1.5">From date</label>
            <DatePicker
                id="startDate" selected={startDate}
                onChange={date => { if (date != null) setStartDate(date) }}
                selectsStart startDate={startDate} endDate={endDate} isClearable
                placeholderText="Start date" className="zbh-input w-48"
            />
          </div>
          <div className="flex flex-col">
            <label htmlFor="endDate" className="zbh-eyebrow mb-1.5">To date</label>
            <DatePicker
                id="endDate" selected={endDate}
                onChange={date => { if (date != null) setEndDate(date) }}
                selectsEnd startDate={startDate} endDate={endDate} isClearable
                placeholderText="End date" className="zbh-input w-48"
            />
          </div>
          <button className="zbh-btn-ghost ml-auto" onClick={() => { fetchUserReport(); }}>
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 4v11M8 11l4 4 4-4" /><path d="M5 19h14" />
            </svg>
            Download user report
          </button>
        </div>
      </div>

      {/* KPI receipt chits */}
      <div className="w-11/12 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="zbh-card p-6">
          <div className="flex items-center justify-between">
            <p className="zbh-eyebrow">Income</p>
            <span className="h-2.5 w-2.5 rounded-full bg-accent" />
          </div>
          <div className="zbh-tear my-3" />
          <p className="zbh-figure text-4xl font-bold text-ink">${income.toFixed(2)}</p>
          <p className="mt-1 text-xs text-categoriesTextColor">Net over selected range</p>
        </div>
        <div className="zbh-card p-6">
          <div className="flex items-center justify-between">
            <p className="zbh-eyebrow">Orders delivered</p>
            <span className="h-2.5 w-2.5 rounded-full bg-state-delivered" />
          </div>
          <div className="zbh-tear my-3" />
          <p className="zbh-figure text-4xl font-bold text-ink">{ordersDelivered}</p>
          <p className="mt-1 text-xs text-categoriesTextColor">Completed tickets</p>
        </div>
      </div>

      {/* Delivered orders table */}
      <div className="zbh-card w-11/12 p-6 mb-6">
        <h2 className="font-display text-base font-semibold text-ink">Delivered orders</h2>
        <div className="mt-4 grid grid-cols-12 gap-4 px-2">
          <div className="zbh-th col-span-4">Placed</div>
          <div className="zbh-th col-span-3">Customer</div>
          <div className="zbh-th col-span-2">Status</div>
          <div className="zbh-th col-span-2 text-right">Total</div>
          <div className="zbh-th col-span-1 text-right">&nbsp;</div>
        </div>
        <div className="zbh-tear mt-2" />
        {orders.map((order, index) => {
          const m = STATUS_META[order.state] ?? STATUS_META.DELIVERED;
          return (
            <div
                key={order.id}
                className="grid grid-cols-12 items-center gap-4 px-2 py-3 border-b border-hair last:border-0 transition hover:bg-canvas/60 rounded-lg"
                ref={index === orders.length - 1 ? lastItemRef : null}
            >
              <div className="col-span-12 md:col-span-4">
                <span className="zbh-figure text-sm text-ink">
                  {new Date(order.order_date).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', month: 'short', day: '2-digit', year: 'numeric' })}
                </span>
              </div>
              <div className="col-span-6 md:col-span-3 min-w-0">
                <p className="truncate text-sm font-medium text-ink">{order.user?.name}</p>
                <p className="zbh-figure text-xs text-categoriesTextColor">{order.user?.phone_number}</p>
              </div>
              <div className="col-span-6 md:col-span-2">
                <span className={`zbh-chip ${m.bg} ${m.text}`}>{m.label}</span>
              </div>
              <div className="col-span-6 md:col-span-2 md:text-right">
                <span className="zbh-figure text-sm font-bold text-ink">${order.total}</span>
              </div>
              <div className="col-span-6 md:col-span-1 md:text-right">
                <Link to={`/orders/view/${order.id}`} className="zbh-link text-sm">View</Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  )
}

export default Analytics
